import { useEffect, useMemo, useState } from "react";
import { getWorkOrderApprovals } from "../../api/reports";
import {
  approveProductionOrderOperation,
  completeProductionOrderOperation,
  getProductionOrderOperationRequirements,
  startApprovalSession,
} from "../../api/productionOrders";
import { reverseOperationApproval } from "../../api/reversal";
import { listInventoryBalances } from "../../api/inventory";
import { Alert, Button, Paper, Stack, TextField, Typography } from "@mui/material";
import { PageHeader } from "../../components/ui";
import { formatReservationLotNo } from "../../utils/reservationLot";

function fmtDate(val) {
  if (!val) return "";
  return String(val).replace("T", " ").slice(0, 19);
}

const KOLILEME_OUTER_BOX_MATERIAL_IDS = [46, 10]; // hammadde dış koli etiket, hammadde dış koli

/** Kuyrukta bıçak + sheet_target ile gösterim: hedef/üretim/kalan tabaka (sayı). */
function isCuttingOpCode(operationCode) {
  const c = String(operationCode || "").toLowerCase();
  return c.includes("kesim") || c.includes("cut");
}

function isAyiklamaOpCode(operationCode) {
  const c = String(operationCode || "").toLowerCase();
  return c.includes("ayık") || c.includes("ayik");
}

function cuttingSheetViewFields(op) {
  const isCutting = isCuttingOpCode(op.operation_code);
  const ppc =
    op.planned_knife_plates_count != null && op.planned_knife_plates_count !== ""
      ? Number(op.planned_knife_plates_count)
      : null;
  const sheetTarget =
    op.queue_sheet_target != null && op.queue_sheet_target !== "" ? Number(op.queue_sheet_target) : null;
  const use =
    isCutting && sheetTarget != null && Number.isFinite(sheetTarget) && ppc != null && Number.isFinite(ppc) && ppc > 0;
  const qtyTargetPlates = Number(op.qty_target || 0);
  const qtyDonePlates = Number(op.qty_done || 0);
  const target = use ? sheetTarget : op.qty_target;
  // qty_done tabaka (sheet) cinsinden saklanıyor
  const done = qtyDonePlates;
  const remaining = use ? sheetTarget - qtyDonePlates : qtyTargetPlates - qtyDonePlates;
  let fire = null;
  if (op.complete_fire_qty != null && op.complete_fire_qty !== "") {
    fire = Number(op.complete_fire_qty);
  }
  return { useSheetView: use, displayTarget: target, displayDone: done, displayRemaining: remaining, displayFire: fire };
}

/** Kesim + kuyruk: onay formu tabaka girer; gereksinim API'si plaka ister. */
function cuttingUseSheetInput(op) {
  const ppc =
    op.planned_knife_plates_count != null && op.planned_knife_plates_count !== ""
      ? Number(op.planned_knife_plates_count)
      : null;
  return (
    isCuttingOpCode(op.operation_code) &&
    Number.isFinite(ppc) &&
    ppc > 0 &&
    op.queue_sheet_target != null &&
    op.queue_sheet_target !== ""
  );
}

function qtyForRequirementsApi(op, qtyFromForm) {
  const n = Number(qtyFromForm);
  if (!Number.isFinite(n) || n <= 0) return Math.max(0, Math.round(n));
  if (cuttingUseSheetInput(op)) {
    const ppc = Number(op.planned_knife_plates_count);
    return Math.round(n * ppc);
  }
  return Math.round(n);
}

/** İş merkezi grubu atanmışsa planlama kuyruğu (+ kesimde bıçak) olmadan Başlatılmaz. */
function canStartApprovalSession(op) {
  const hasGroup =
    op.work_center_group_id != null &&
    op.work_center_group_id !== "" &&
    Number.isFinite(Number(op.work_center_group_id)) &&
    Number(op.work_center_group_id) > 0;
  if (!hasGroup) return true; // İş merkezi grubu yok → serbest
  const inQueue =
    op.planning_queue_row_id != null && op.planning_queue_row_id !== "" && Number(op.planning_queue_row_id) > 0;
  const cutting = isCuttingOpCode(op.operation_code);
  const knifeOk =
    !cutting ||
    (op.planned_knife_id != null &&
      op.planned_knife_id !== "" &&
      Number.isFinite(Number(op.planned_knife_id)) &&
      Number(op.planned_knife_id) > 0);
  return inQueue && knifeOk;
}

export default function WorkOrderApprovalSearchPage() {
  const [orderNo, setOrderNo] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const [expandedOps, setExpandedOps] = useState(() => new Set()); // routing_operation_id

  const [activeOpId, setActiveOpId] = useState(null); // routing_operation_id
  const [activeOpCode, setActiveOpCode] = useState(""); // operation_code of active op
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState("");
  const [qtyDone, setQtyDone] = useState("");
  const [requirements, setRequirements] = useState([]);
  const [availableLots, setAvailableLots] = useState([]);
  const [consumptions, setConsumptions] = useState([]); // { material_id, material_variant_id, lot_no, qty }

  const [reversalModal, setReversalModal] = useState(null); // { approvalId, blockers }
  const [reversalLoading, setReversalLoading] = useState(false);
  const [reversalError, setReversalError] = useState("");

  function handleReverse(approvalId) {
    setReversalModal({ approvalId, blockers: null });
    setReversalError("");
  }

  async function onConfirmReversal() {
    setReversalLoading(true);
    setReversalError("");
    try {
      await reverseOperationApproval(reversalModal.approvalId, "");
      setReversalModal(null);
      await refreshReport();
    } catch (err) {
      const msg = err?.response?.data?.error?.message || err?.message || "Hata";
      setReversalError(msg);
    } finally {
      setReversalLoading(false);
    }
  }

  const [coilLots, setCoilLots] = useState([]);
  const [selectedCoilMaterialId, setSelectedCoilMaterialId] = useState("");
  const [selectedCoilLotNo, setSelectedCoilLotNo] = useState("");
  const [baskiPrintedMeter, setBaskiPrintedMeter] = useState("");
  const [baskiScrapMaterialId, setBaskiScrapMaterialId] = useState("");
  const isCuttingOp = /kesim/i.test(activeOpCode);
  const isBaskiOp = /bask/i.test(activeOpCode);

  // Load coil lots for both cutting and baskı operations
  useEffect(() => {
    if (!isCuttingOp && !isBaskiOp) return;
    listInventoryBalances({})
      .then((rows) => {
        const coils = (rows || []).filter(
          (r) =>
            String(r.material_unit || "").toLowerCase() === "m" &&
            Number(r.on_hand_qty || 0) > 0 &&
            r.coil_remaining_length_mt !== null &&
            r.coil_remaining_length_mt !== undefined
        );
        setCoilLots(coils);
      })
      .catch((err) => console.error("COIL LOTS LOAD ERROR", err));
  }, [isCuttingOp, isBaskiOp]);

  const operations = useMemo(() => {
    return Array.isArray(data?.operations) ? data.operations : [];
  }, [data]);

  const flatRows = useMemo(() => {
    const out = [];
    for (const op of operations) {
      out.push({ type: "op", key: `op:${op.routing_operation_id}`, op });
      if (expandedOps.has(op.routing_operation_id)) {
        const approvals = Array.isArray(op.approvals) ? op.approvals : [];
        for (const a of approvals) {
          out.push({
            type: "approval",
            key: `appr:${op.routing_operation_id}:${a.approval_id}`,
            op,
            approval: a,
          });
        }
      }
    }
    return out;
  }, [operations, expandedOps]);

  async function onSearch(e) {
    e?.preventDefault?.();
    setError("");
    setData(null);
    setExpandedOps(new Set());
    setActiveOpId(null);
    setFormError("");

    const q = orderNo.trim();
    if (!q) {
      setError("İş Emri No zorunlu");
      return;
    }

    setLoading(true);
    try {
      const res = await getWorkOrderApprovals(q);
      setData(res);
    } catch (err) {
      console.error("WORK ORDER APPROVALS LOAD ERROR", err);
      const status = err?.response?.status;
      const data = err?.response?.data;
      const message =
        data?.error?.message ||
        data?.message ||
        (typeof data === "string" ? data : null) ||
        err?.message ||
        null;
      setError(`Rapor alınamadı${status ? ` (HTTP ${status})` : ""}${message ? `: ${message}` : ""}`);
    } finally {
      setLoading(false);
    }
  }

  function toggleOp(routingOperationId) {
    setExpandedOps((prev) => {
      const next = new Set(prev);
      if (next.has(routingOperationId)) next.delete(routingOperationId);
      else next.add(routingOperationId);
      return next;
    });
  }

  function resetForm() {
    setFormError("");
    setQtyDone("");
    setRequirements([]);
    setAvailableLots([]);
    setConsumptions([]);
    setSelectedCoilMaterialId("");
    setSelectedCoilLotNo("");
    setBaskiPrintedMeter("");
    setBaskiScrapMaterialId("");
  }

  function findLotsForMaterial(materialId, materialVariantId) {
    // If requirement doesn't specify variant, backend may return lots across variants in the same entry.
    const item = (availableLots || []).find((x) => Number(x.material_id) === Number(materialId));
    if (!item) return [];
    if (materialVariantId === null || materialVariantId === undefined) return Array.isArray(item?.lots) ? item.lots : [];
    // Variant-specific requirement: filter lots that match the variant
    const lots = Array.isArray(item?.lots) ? item.lots : [];
    return lots.filter((l) => (l.material_variant_id ?? null) === (materialVariantId ?? null));
  }

  function initConsumptionsFromRequirements(reqs) {
    const out = [];
    for (const [idx, r] of (reqs || []).entries()) {
      out.push({
        req_index: idx,
        material_id: Number(r.material_id),
        material_variant_id: r.material_variant_id ?? null,
        lot_no: "",
        qty: Number(r.qty || 0),
      });
    }
    setConsumptions(out);
  }

  async function openApproveForm(op) {
    const productionOrderId = data?.header?.production_order_id;
    const routingOperationId = op?.routing_operation_id;
    if (!productionOrderId || !routingOperationId) return;

    setActiveOpId(routingOperationId);
    setActiveOpCode(op.operation_code || "");
    setFormError("");
    setFormLoading(true);

    const remainingSheets = Number(op.qty_target || 0) - Number(op.qty_done || 0);
    const useSheet = cuttingUseSheetInput(op);
    let defaultQtyDisplay;
    if (useSheet) {
      const sheetTarget = Number(op.queue_sheet_target);
      defaultQtyDisplay = Math.max(0, sheetTarget - (Number(op.qty_done || 0)));
    } else {
      defaultQtyDisplay = Math.max(0, remainingSheets);
    }
    setQtyDone(String(defaultQtyDisplay > 0 ? Math.round(defaultQtyDisplay) : 0));

    const qtyForReq =
      defaultQtyDisplay > 0
        ? qtyForRequirementsApi(op, defaultQtyDisplay)
        : Math.max(1, qtyForRequirementsApi(op, 1));

    try {
      const reqRes = await getProductionOrderOperationRequirements(
        productionOrderId,
        routingOperationId,
        qtyForReq
      );
      let reqs = Array.isArray(reqRes?.requirements) ? reqRes.requirements : [];
      let lots = Array.isArray(reqRes?.available_lots) ? reqRes.available_lots : [];
      const isKolileme = /koli/i.test(op.operation_code || "");
      const dim31 = reqRes?.dimension_31_value;
      if (isKolileme && (dim31 == null || dim31 <= 0)) {
        reqs = reqs.filter((r) => !KOLILEME_OUTER_BOX_MATERIAL_IDS.includes(Number(r.material_id)));
        lots = lots.filter((l) => !KOLILEME_OUTER_BOX_MATERIAL_IDS.includes(Number(l.material_id)));
      }
      setRequirements(reqs);
      setAvailableLots(lots);
      initConsumptionsFromRequirements(reqs);
      setExpandedOps((prev) => new Set(prev).add(routingOperationId));
    } catch (err) {
      console.error("REQUIREMENTS LOAD ERROR", err);
      const status = err?.response?.status;
      const rdata = err?.response?.data;
      const message =
        rdata?.error?.message ||
        rdata?.message ||
        (typeof rdata === "string" ? rdata : null) ||
        err?.message ||
        null;
      setFormError(`Gereksinimler alınamadı${status ? ` (HTTP ${status})` : ""}${message ? `: ${message}` : ""}`);
      setRequirements([]);
      setAvailableLots([]);
      setConsumptions([]);
    } finally {
      setFormLoading(false);
    }
  }

  async function refreshReport() {
    const q = orderNo.trim();
    if (!q) return;
    const res = await getWorkOrderApprovals(q);
    setData(res);
  }

  async function handleCompleteOp(op) {
    const productionOrderId = data?.header?.production_order_id;
    const rid = Number(op?.routing_operation_id);
    if (!productionOrderId || !Number.isFinite(rid)) return;

    const target = Math.max(0, Number(op?.qty_target || 0));
    const done = Math.max(0, Number(op?.qty_done || 0));
    const fireQtyNow = Math.max(0, target - done);
    const fireThreshold = target > 0 ? target * 0.01 : 0;
    const needsReason = fireQtyNow > fireThreshold;

    let fireReason = null;
    if (needsReason) {
      const pct = target > 0 ? ((fireQtyNow / target) * 100).toFixed(1) : "?";
      const reason = window.prompt(
        `Fire miktarı ${fireQtyNow.toLocaleString("tr-TR")} adet (hedefin %${pct}'i).\nLütfen fire nedenini açıklayın:`
      );
      if (reason == null) return; // iptal
      if (!reason.trim()) {
        alert("Fire nedeni boş bırakılamaz.");
        return;
      }
      fireReason = reason.trim();
    }

    const confirmMsg = needsReason
      ? `Fire: ${fireQtyNow.toLocaleString("tr-TR")} adet\nNeden: ${fireReason}\n\nOperasyonu komple bitirmek istediğinize emin misiniz?`
      : "Bu operasyonu komple bitirmek istediğinize emin misiniz?";
    const ok = window.confirm(confirmMsg);
    if (!ok) return;
    try {
      setFormLoading(true);
      const res = await completeProductionOrderOperation(productionOrderId, rid, fireReason);
      const msgParts = [];
      if (res?.warning) msgParts.push(String(res.warning));
      const fq = res?.fire_qty;
      if (fq != null && Number(fq) > 0) msgParts.push(`Fire: ${Number(fq)}`);
      const sk = res?.scrap_kg;
      if (sk != null && Number(sk) > 0) msgParts.push(`${Number(sk).toFixed(2)} kg hurda stoğa alındı`);
      if (msgParts.length) alert(msgParts.join(" | "));
      await refreshReport();
    } catch (err) {
      const status = err?.response?.status;
      const rdata = err?.response?.data;
      const errCode = rdata?.error?.code;
      const message =
        rdata?.error?.message ||
        rdata?.message ||
        (typeof rdata === "string" ? rdata : null) ||
        err?.message ||
        null;
      if (status === 409 && errCode === "QC_PENDING") {
        alert(
          'Kalite onayı bekleniyor. Önce "Kalite Kontrol Onayları" ekranından QC onayı verin; ardından komple bitirebilirsiniz.'
        );
      } else {
        alert(message || "Komple bitirme başarısız");
      }
    } finally {
      setFormLoading(false);
    }
  }

  async function handleStartApproval(op) {
    const productionOrderId = data?.header?.production_order_id;
    const rid = Number(op?.routing_operation_id);
    if (!productionOrderId || !Number.isFinite(rid)) return;
    try {
      setFormLoading(true);
      await startApprovalSession(productionOrderId, rid);
      await refreshReport();
    } catch (err) {
      const status = err?.response?.status;
      const rdata = err?.response?.data;
      const message =
        rdata?.error?.message ||
        rdata?.message ||
        (typeof rdata === "string" ? rdata : null) ||
        err?.message ||
        null;
      alert(message || `Başlatma başarısız${status ? ` (HTTP ${status})` : ""}`);
    } finally {
      setFormLoading(false);
    }
  }

  async function onApprove() {
    const productionOrderId = data?.header?.production_order_id;
    const routingOperationId = activeOpId;
    if (!productionOrderId || !routingOperationId) return;

    setFormError("");
    setFormLoading(true);

    try {
      const payload = {
        qty_done: Number(qtyDone),
        consumptions: (consumptions || [])
          .filter((c) => c && String(c.lot_no || "").trim())
          .map((c) => ({
            material_id: Number(c.material_id),
            material_variant_id: c.material_variant_id ?? null,
            lot_no: String(c.lot_no).trim(),
            qty: Number(c.qty || 0),
          })),
      };
      if (baskiPrintedMeter) {
        payload.baski_printed_meter = Number(baskiPrintedMeter);
      }
      if (baskiScrapMaterialId) {
        payload.baski_scrap_material_id = Number(baskiScrapMaterialId);
      }

      await approveProductionOrderOperation(productionOrderId, routingOperationId, payload);
      await refreshReport();
      resetForm();
      setActiveOpId(null);
      setActiveOpCode("");
    } catch (err) {
      console.error("APPROVE ERROR", err);
      const status = err?.response?.status;
      const rdata = err?.response?.data;
      const errCode = rdata?.error?.code;
      const message =
        rdata?.error?.message ||
        rdata?.message ||
        (typeof rdata === "string" ? rdata : null) ||
        err?.message ||
        null;
      if (status === 409 && errCode === "QC_PENDING") {
        setFormError('Kalite onayı bekleniyor. Önce "Kalite Kontrol Onayları" ekranından QC onayı verin.');
      } else {
        setFormError(`Onay başarısız${status ? ` (HTTP ${status})` : ""}${message ? `: ${message}` : ""}`);
      }
    } finally {
      setFormLoading(false);
    }
  }

  return (
    <Stack spacing={2}>
      <PageHeader
        title="İş Bitiş Onayı Arama"
        subtitle="İş emri operasyonlarını görüntüleyin ve onaylayın."
        actions={
          <Stack
            component="form"
            onSubmit={onSearch}
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            sx={{ width: { xs: "100%", md: "auto" } }}
          >
            <TextField
              label="İş Emri No"
              value={orderNo}
              onChange={(e) => setOrderNo(e.target.value)}
              placeholder="WO-..."
              sx={{ minWidth: { xs: "100%", sm: 260 } }}
            />
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? "Aranıyor..." : "Ara"}
            </Button>
          </Stack>
        }
      />

      {error ? <Alert severity="error">{error}</Alert> : null}

      {data?.header ? (
        <Paper sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            <b>İş Emri:</b> {data.header.order_no}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            <b>Ürün:</b> {data.header.product_material_code || ""}{" "}
            {data.header.product_material_name || data.header.product_name || ""}
            {data.header.product_variant_display_name
              ? ` (${data.header.product_variant_display_name})`
              : ""}
          </Typography>
        </Paper>
      ) : null}

      {flatRows.length ? (
        <Paper sx={{ p: 2, overflowX: "auto" }}>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Kesim + kuyruk + bıçak: hedef / üretim / kalan / fire tabakadır (kesilmiş yarı mamul çıktısı).
            Kesim onayında girilen miktar tabaka; bobin tüketimi plaka ve bıçak parametreleriyle hesaplanır.
            Ayıklama: hedef kesimdeki tabaka adedidir; onay miktarı tabaka, stoktan düşen kesilmiş tabaka ve stoğa giren
            ayıklanmış plaka = tabaka × (bıçaktaki tabak sayısı).
          </Typography>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left", borderBottom: "1px solid #e5e7eb", padding: 8 }}>
                  Operasyon
                </th>
                <th style={{ textAlign: "left", borderBottom: "1px solid #e5e7eb", padding: 8 }}>
                  İş Merkezi
                </th>
                <th style={{ textAlign: "right", borderBottom: "1px solid #e5e7eb", padding: 8 }}>
                  Hedef Miktar
                </th>
                <th style={{ textAlign: "right", borderBottom: "1px solid #e5e7eb", padding: 8 }}>
                  Üretim Miktarı
                </th>
                <th style={{ textAlign: "right", borderBottom: "1px solid #e5e7eb", padding: 8 }}>Kalan</th>
                <th style={{ textAlign: "right", borderBottom: "1px solid #e5e7eb", padding: 8 }}>Fire</th>
                <th style={{ textAlign: "left", borderBottom: "1px solid #e5e7eb", padding: 8 }}>Durum</th>
                <th style={{ textAlign: "left", borderBottom: "1px solid #e5e7eb", padding: 8 }}>Başlangıç</th>
                <th style={{ textAlign: "left", borderBottom: "1px solid #e5e7eb", padding: 8 }}>Bitiş</th>
                <th style={{ textAlign: "right", borderBottom: "1px solid #e5e7eb", padding: 8 }}>Onay</th>
              </tr>
            </thead>
            <tbody>
              {flatRows.map((row) => {
                if (row.type === "op") {
                  const op = row.op;
                  const wcLabel = op.work_center
                    ? `${op.work_center.code || ""} ${op.work_center.name || ""}`.trim()
                    : "";
                  const sheetF = cuttingSheetViewFields(op);
                  const { displayTarget, displayDone, displayRemaining, displayFire, useSheetView } = sheetF;
                  const remaining = Number(op.qty_target || 0) - Number(op.qty_done || 0);
                  const isExpanded = expandedOps.has(op.routing_operation_id);
                  const isActive = activeOpId === op.routing_operation_id;
                  const isBaskiRow = /bask/i.test(op.operation_code || "");
                  const startOk = canStartApprovalSession(op);

                  return (
                    <>
                      <tr key={row.key}>
                        <td style={{ borderBottom: "1px solid #f3f4f6", padding: 8 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <button
                              type="button"
                              onClick={() => toggleOp(op.routing_operation_id)}
                              style={{
                                border: "1px solid #e5e7eb",
                                background: "#fff",
                                borderRadius: 6,
                                width: 26,
                                height: 26,
                                cursor: "pointer",
                              }}
                              title={isExpanded ? "Kapat" : "Aç"}
                            >
                              {isExpanded ? "-" : "+"}
                            </button>
                            <div>
                              <div style={{ fontWeight: 600 }}>
                                OP {op.op_no} {op.operation_code ? `(${op.operation_code})` : ""}
                              </div>
                              <div style={{ color: "#6b7280" }}>{op.operation_description || ""}</div>
                            </div>
                          </div>
                        </td>
                        <td style={{ borderBottom: "1px solid #f3f4f6", padding: 8 }}>{wcLabel || "—"}</td>
                        <td style={{ borderBottom: "1px solid #f3f4f6", padding: 8, textAlign: "right" }}>
                          {isBaskiRow ? "—" : useSheetView ? Math.round(displayTarget) : Math.round(Number(op.qty_target || 0))}
                        </td>
                        <td style={{ borderBottom: "1px solid #f3f4f6", padding: 8, textAlign: "right" }}>
                          {isBaskiRow ? "—" : useSheetView ? Math.round(displayDone) : Math.round(Number(op.qty_done || 0))}
                        </td>
                        <td style={{ borderBottom: "1px solid #f3f4f6", padding: 8, textAlign: "right" }}>
                          {isBaskiRow ? "—" : useSheetView ? Math.round(displayRemaining) : Math.round(remaining)}
                        </td>
                        <td style={{ borderBottom: "1px solid #f3f4f6", padding: 8, textAlign: "right" }}>
                          {isBaskiRow
                            ? "—"
                            : useSheetView
                              ? displayFire != null && Number.isFinite(displayFire)
                                ? Math.round(displayFire)
                                : "—"
                              : op.complete_fire_qty != null && op.complete_fire_qty !== ""
                                ? Number(op.complete_fire_qty)
                                : "—"}
                        </td>
                        <td style={{ borderBottom: "1px solid #f3f4f6", padding: 8 }}>{op.status || "—"}</td>
                        <td style={{ borderBottom: "1px solid #f3f4f6", padding: 8 }}>{fmtDate(op.started_at) || "—"}</td>
                        <td style={{ borderBottom: "1px solid #f3f4f6", padding: 8 }}>{fmtDate(op.finished_at) || "—"}</td>
                        <td style={{ borderBottom: "1px solid #f3f4f6", padding: 8, textAlign: "right" }}>
                          {(() => {
                            const opStatus = String(op.status);
                            const hasOpenSession = !!op.has_open_session;
                            const startDisabled = formLoading || opStatus === "finished" || hasOpenSession || !startOk;
                            const startBg = startDisabled ? "#ccc" : "#16a34a";
                            const hasGroup =
                              op.work_center_group_id != null &&
                              Number.isFinite(Number(op.work_center_group_id)) &&
                              Number(op.work_center_group_id) > 0;
                            const inQueue =
                              op.planning_queue_row_id != null &&
                              Number(op.planning_queue_row_id) > 0;
                            const startTitle = hasOpenSession
                              ? "Önce mevcut onay oturumunu tamamlayın."
                              : !startOk && hasGroup && !inQueue
                                ? "Önce planlama ekranından bu operasyona iş merkezi atayın."
                                : !startOk
                                  ? "Kesim operasyonu için bıçak atanmış olmalı."
                                  : "Operasyona başla (oturum aç)";
                            return (
                              <button
                                type="button"
                                onClick={() => handleStartApproval(op)}
                                disabled={startDisabled}
                                style={{
                                  border: "none",
                                  background: startBg,
                                  color: "#fff",
                                  borderRadius: 6,
                                  padding: "6px 10px",
                                  cursor: startDisabled ? "default" : "pointer",
                                  fontSize: 12,
                                  marginRight: 4,
                                }}
                                title={startTitle}
                              >
                                Başlat
                              </button>
                            );
                          })()}
                          <button
                            type="button"
                            onClick={() => {
                              if (isActive) {
                                setActiveOpId(null);
                                setActiveOpCode("");
                                resetForm();
                              } else {
                                resetForm();
                                openApproveForm(op);
                              }
                            }}
                            disabled={formLoading}
                            style={{
                              border: "1px solid #e5e7eb",
                              background: "#fff",
                              borderRadius: 6,
                              padding: "6px 10px",
                              cursor: "pointer",
                            }}
                            title="Onay formunu aç"
                          >
                            +
                          </button>
                          {" "}
                          <button
                            type="button"
                            onClick={() => handleCompleteOp(op)}
                            disabled={formLoading || String(op.status) !== "started"}
                            style={{
                              border: "none",
                              background: String(op.status) === "started" ? "#d32f2f" : "#ccc",
                              color: "#fff",
                              borderRadius: 6,
                              padding: "6px 10px",
                              cursor: String(op.status) === "started" ? "pointer" : "default",
                              fontSize: 12,
                            }}
                            title="Operasyonu komple bitir"
                          >
                            Komple Bitti
                          </button>
                        </td>
                      </tr>

                      {isActive ? (
                        <tr key={`${row.key}:form`}>
                          <td colSpan={10} style={{ borderBottom: "1px solid #f3f4f6", padding: 12, background: "#fafafa" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                              <div style={{ fontWeight: 600 }}>Operasyon Onayı</div>
                              <button
                                type="button"
                                onClick={() => {
                                  setActiveOpId(null);
                                  setActiveOpCode("");
                                  resetForm();
                                }}
                                style={{
                                  border: "1px solid #e5e7eb",
                                  background: "#fff",
                                  borderRadius: 6,
                                  padding: "6px 10px",
                                  cursor: "pointer",
                                }}
                              >
                                Kapat
                              </button>
                            </div>

                            {formError ? <div style={{ color: "crimson", marginTop: 8 }}>{formError}</div> : null}

                            {requirements.length > 0 &&
                            (availableLots.length === 0 ||
                              availableLots.every((a) => !Array.isArray(a?.lots) || a.lots.length === 0)) ? (
                              <Alert severity="info" sx={{ mt: 1 }}>
                                Uygun lot listelenmiyor. Satış siparişine bağlı iş emirlerinde bazı malzemeler (ör.
                                etiket, koli) sipariş kalemiyle filtrelenir; stok ve giriş kayıtlarını kontrol edin.
                              </Alert>
                            ) : null}

                            <div style={{ display: "flex", gap: 12, alignItems: "center", marginTop: 10, flexWrap: "wrap" }}>
                              <label>
                                {(() => {
                                  const active = operations.find((o) => o.routing_operation_id === activeOpId);
                                  const labelSheet =
                                    active && cuttingUseSheetInput(active)
                                      ? "Onay miktarı (tabaka — kesilmiş yarı mamul)"
                                      : active && isAyiklamaOpCode(active.operation_code)
                                        ? "Onay miktarı (tabaka — kesilmiş bobin/tabaka)"
                                        : "Onay Miktarı (qty)";
                                  return (
                                    <>
                                      {labelSheet}{" "}
                                      <input
                                        type="number"
                                        min="1"
                                        value={qtyDone}
                                        onChange={(e) => setQtyDone(e.target.value)}
                                        style={{ padding: 8, width: 220 }}
                                      />
                                    </>
                                  );
                                })()}
                              </label>
                              <button
                                type="button"
                                disabled={formLoading || !qtyDone}
                                onClick={async () => {
                                  setFormError("");
                                  setFormLoading(true);
                                  try {
                                    const active = operations.find((o) => o.routing_operation_id === activeOpId);
                                    const qtyPlates = active
                                      ? qtyForRequirementsApi(active, Number(qtyDone))
                                      : Number(qtyDone);
                                    const reqRes = await getProductionOrderOperationRequirements(
                                      data.header.production_order_id,
                                      activeOpId,
                                      qtyPlates
                                    );
                                    let reqs = Array.isArray(reqRes?.requirements) ? reqRes.requirements : [];
                                    let lots = Array.isArray(reqRes?.available_lots) ? reqRes.available_lots : [];
                                    const isKolilemeRefresh = /koli/i.test(activeOpCode || "");
                                    const dim31 = reqRes?.dimension_31_value;
                                    if (isKolilemeRefresh && (dim31 == null || dim31 <= 0)) {
                                      reqs = reqs.filter((r) => !KOLILEME_OUTER_BOX_MATERIAL_IDS.includes(Number(r.material_id)));
                                      lots = lots.filter((l) => !KOLILEME_OUTER_BOX_MATERIAL_IDS.includes(Number(l.material_id)));
                                    }
                                    setRequirements(reqs);
                                    setAvailableLots(lots);
                                    initConsumptionsFromRequirements(reqs);
                                  } catch (err) {
                                    console.error("REQUIREMENTS REFRESH ERROR", err);
                                    const status = err?.response?.status;
                                    const rdata = err?.response?.data;
                                    const message =
                                      rdata?.error?.message ||
                                      rdata?.message ||
                                      (typeof rdata === "string" ? rdata : null) ||
                                      err?.message ||
                                      null;
                                    setFormError(
                                      `Gereksinimler alınamadı${status ? ` (HTTP ${status})` : ""}${message ? `: ${message}` : ""
                                      }`
                                    );
                                  } finally {
                                    setFormLoading(false);
                                  }
                                }}
                                style={{
                                  border: "1px solid #e5e7eb",
                                  background: "#fff",
                                  borderRadius: 6,
                                  padding: "8px 12px",
                                  cursor: "pointer",
                                }}
                              >
                                Gereksinimleri Getir
                              </button>
                            </div>

                            <div style={{ marginTop: 12 }}>
                              <div style={{ fontWeight: 600, marginBottom: 8 }}>Komponent Tüketimleri</div>
                              {requirements.length ? (
                                <div style={{ display: "grid", gap: 10 }}>
                                  {requirements.map((reqItem, reqIdx) => {
                                    const matId = Number(reqItem.material_id);
                                    const varId = reqItem.material_variant_id ?? null;
                                    const materialName = String(reqItem.material_name || "").trim();
                                    const lots = findLotsForMaterial(matId, varId);
                                    const reqQty = Number(reqItem.qty || 0);

                                    const relatedIdxs = consumptions
                                      .map((c, idx) => ({ c, idx }))
                                      .filter(
                                        ({ c }) =>
                                          c.req_index === reqIdx ||
                                          (c.req_index === undefined &&
                                            Number(c.material_id) === matId &&
                                            (c.material_variant_id ?? null) === (varId ?? null))
                                      );

                                    console.log(`[LOT_DEBUG] reqIdx: ${reqIdx}, matId: ${matId} - Found ${relatedIdxs.length} rows`);

                                    return (
                                      <div
                                        key={`${matId}:${varId ?? 0}`}
                                        style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: 10, background: "#fff" }}
                                      >
                                        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
                                          <div>
                                            <div style={{ fontWeight: 600 }}>
                                              {materialName || `Malzeme #${matId}`}
                                              {varId ? ` / Varyant #${varId}` : ""}
                                            </div>
                                            <div style={{ color: "#6b7280" }}>Gerekli: {reqItem.uom === "kg" ? reqQty.toFixed(1) : Math.round(reqQty)} {reqItem.uom && reqItem.uom !== "adet" ? reqItem.uom : ""}</div>
                                          </div>
                                          <button
                                            type="button"
                                            onClick={() => {
                                              setConsumptions((prev) => [
                                                ...prev,
                                                { req_index: reqIdx, material_id: matId, material_variant_id: varId, lot_no: "", qty: 0 },
                                              ]);
                                            }}
                                            style={{
                                              border: "1px solid #e5e7eb",
                                              background: "#fff",
                                              borderRadius: 6,
                                              padding: "6px 10px",
                                              cursor: "pointer",
                                            }}
                                          >
                                            + Lot Satırı
                                          </button>
                                        </div>

                                        {relatedIdxs.length ? (
                                          <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
                                            {relatedIdxs.map(({ c, idx }) => (
                                              <div key={idx} style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                                                <label>
                                                  Lot{" "}
                                                  <select
                                                    value={
                                                      c.lot_no
                                                        ? `${c.material_variant_id ?? 0}:${c.lot_no}`
                                                        : ""
                                                    }
                                                    onChange={(e) => {
                                                      const v = e.target.value;
                                                      if (!v) {
                                                        setConsumptions((prev) =>
                                                          prev.map((x, i) =>
                                                            i === idx ? { ...x, lot_no: "", material_variant_id: varId } : x
                                                          )
                                                        );
                                                        return;
                                                      }
                                                      const [vKeyStr, ...lotParts] = String(v).split(":");
                                                      const vKey = Number(vKeyStr);
                                                      const lotNo = lotParts.join(":");
                                                      const nextVar = vKey === 0 ? null : vKey;
                                                      setConsumptions((prev) =>
                                                        prev.map((x, i) =>
                                                          i === idx ? { ...x, lot_no: lotNo, material_variant_id: nextVar } : x
                                                        )
                                                      );
                                                      // Kağıt lotu seçilince lak/boya miktarını hesapla
                                                      const lotGroup = availableLots.find((al) => Number(al.material_id) === matId);
                                                      if (lotGroup?.is_paper) {
                                                        const selectedLot = lots.find((l) => l.lot_no === lotNo);
                                                        const widthCm = Number(selectedLot?.width_cm || 0);
                                                        if (widthCm > 0) {
                                                          const paperQty = Number(reqQty || 0);
                                                          const inkLakKg = (paperQty * (widthCm / 100) * 10) / 1000;
                                                          setRequirements((prev) =>
                                                            prev.map((r) => {
                                                              const rRaw = String(r.raw_material_type || "");
                                                              const isInkLak = (rRaw === "ink" || rRaw === "lak" || rRaw.includes("lak") || rRaw === "flekso_lak" || rRaw === "flexo_lak");
                                                              return isInkLak ? { ...r, qty: inkLakKg } : r;
                                                            })
                                                          );
                                                        }
                                                      }
                                                    }}
                                                    style={{ padding: 8, minWidth: 220 }}
                                                  >
                                                    <option value="">Seçiniz</option>
                                                    {lots.map((l) => (
                                                      <option
                                                        key={`${l.material_variant_id ?? 0}:${l.lot_no}`}
                                                        value={`${l.material_variant_id ?? 0}:${l.lot_no}`}
                                                      >
                                                        {formatReservationLotNo(l.lot_no)}
                                                        {l.material_variant_display_name
                                                          ? ` (${l.material_variant_display_name})`
                                                          : ""}
                                                        {" "} (stok: {Number(l.on_hand_qty || 0)})
                                                      </option>
                                                    ))}
                                                  </select>
                                                </label>

                                                {/* Qty input is removed; Backend auto-calculates consumption */}

                                                <button
                                                  type="button"
                                                  onClick={() => setConsumptions((prev) => prev.filter((_, i) => i !== idx))}
                                                  style={{
                                                    border: "1px solid #e5e7eb",
                                                    background: "#fff",
                                                    borderRadius: 6,
                                                    padding: "6px 10px",
                                                    cursor: "pointer",
                                                  }}
                                                >
                                                  Sil
                                                </button>
                                              </div>
                                            ))}
                                          </div>
                                        ) : null}
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : (
                                <div style={{ color: "#6b7280" }}>Bu operasyon için komponent gereksinimi yok.</div>
                              )}
                            </div>

                            <div style={{ marginTop: 12, display: "flex", justifyContent: "flex-end" }}>
                              <button
                                type="button"
                                disabled={formLoading}
                                onClick={onApprove}
                                style={{
                                  border: "1px solid #111827",
                                  background: "#111827",
                                  color: "#fff",
                                  borderRadius: 8,
                                  padding: "10px 14px",
                                  cursor: "pointer",
                                }}
                              >
                                {formLoading ? "Onaylanıyor..." : "Onayla"}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </>
                  );
                }

                const a = row.approval;
                const isOpenSession = a.started_at && !a.approved_at;
                const qcRequired = Number(a.qc_required) === 1;
                const qcStatus = a.qc_status ? String(a.qc_status) : null;
                const qcLabel = qcRequired
                  ? qcStatus === "approved"
                    ? "QC: Onaylandı"
                    : "QC: Bekliyor"
                  : null;
                return (
                  <tr key={row.key} style={isOpenSession ? { background: "#f0fdf4" } : {}}>
                    <td style={{ borderBottom: "1px solid #f3f4f6", padding: 8 }}>
                      <div style={{ paddingLeft: 34, color: "#374151" }}>
                        <div style={{ fontWeight: 600, color: isOpenSession ? "#16a34a" : undefined }}>
                          {isOpenSession ? "Başlatıldı" : "Onay"}
                        </div>
                        {a.started_at ? (
                          <div style={{ color: "#6b7280" }}>
                            Başlangıç: {fmtDate(a.started_at)} • {a.started_by_name || "—"}
                            {a.started_by_employee_code ? ` (${a.started_by_employee_code})` : ""}
                          </div>
                        ) : null}
                        {!isOpenSession ? (
                          <div style={{ color: "#6b7280" }}>
                            {fmtDate(a.approved_at)} • {a.approved_by_name || "—"}
                            {a.approved_by_employee_code ? ` (${a.approved_by_employee_code})` : ""}
                          </div>
                        ) : null}
                        {qcLabel ? (
                          <div style={{ color: qcStatus === "approved" ? "#047857" : "#b45309", marginTop: 2 }}>
                            {qcLabel}
                            {a.qc_checked_at ? ` • ${fmtDate(a.qc_checked_at)}` : ""}
                            {a.qc_checked_by_name ? ` • ${a.qc_checked_by_name}` : ""}
                            {a.qc_checked_by_employee_code ? ` (${a.qc_checked_by_employee_code})` : ""}
                          </div>
                        ) : null}
                        {a.qc_note ? <div style={{ color: "#6b7280", marginTop: 2 }}>QC Not: {String(a.qc_note)}</div> : null}
                      </div>
                    </td>
                    <td style={{ borderBottom: "1px solid #f3f4f6", padding: 8 }}>—</td>
                    <td style={{ borderBottom: "1px solid #f3f4f6", padding: 8, textAlign: "right" }}>—</td>
                    <td style={{ borderBottom: "1px solid #f3f4f6", padding: 8, textAlign: "right" }}>
                      {isOpenSession ? (
                        <span style={{ color: "#16a34a", fontStyle: "italic" }}>devam ediyor</span>
                      ) : a.approved_qty != null ? (() => {
                        const pps = row.op.planned_knife_plates_count;
                        if (isAyiklamaOpCode(row.op.operation_code) && pps > 0) {
                          return Math.round(a.approved_qty / pps);
                        }
                        return a.approved_qty;
                      })() : "—"}
                    </td>
                    <td style={{ borderBottom: "1px solid #f3f4f6", padding: 8, textAlign: "right" }}>—</td>
                    <td style={{ borderBottom: "1px solid #f3f4f6", padding: 8 }}>—</td>
                    <td style={{ borderBottom: "1px solid #f3f4f6", padding: 8 }}>—</td>
                    <td style={{ borderBottom: "1px solid #f3f4f6", padding: 8 }}>
                      {a.output_lot_no ? `Lot: ${a.output_lot_no}` : "—"}
                    </td>
                    <td style={{ borderBottom: "1px solid #f3f4f6", padding: 8, textAlign: "right" }}>
                      {!isOpenSession && a.approval_id && a.status !== "reversed" ? (
                        <button
                          type="button"
                          onClick={() => handleReverse(a.approval_id)}
                          style={{
                            border: "1px solid #dc2626",
                            background: "#fff",
                            color: "#dc2626",
                            borderRadius: 6,
                            padding: "4px 10px",
                            cursor: "pointer",
                            fontSize: 12,
                          }}
                        >
                          Ters Al
                        </button>
                      ) : a.status === "reversed" ? (
                        <span style={{ color: "#9ca3af", fontSize: 12 }}>Ters Alındı</span>
                      ) : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Paper>
      ) : data ? (
        <Typography color="text.secondary">Kayıt bulunamadı</Typography>
      ) : null}

      {/* Reversal Modal */}
      {reversalModal ? (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999
        }}>
          <div style={{
            background: "#fff", borderRadius: 12, padding: 28,
            width: "100%", maxWidth: 400, boxShadow: "0 8px 32px rgba(0,0,0,0.18)"
          }}>
            <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 12 }}>
              Onay #{reversalModal.approvalId} ters alınacak
            </div>
            <div style={{ fontSize: 15, marginBottom: 16 }}>Emin misiniz?</div>

            {reversalError ? (
              <div style={{ color: "crimson", fontSize: 13, marginBottom: 8 }}>{reversalError}</div>
            ) : null}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button
                type="button"
                onClick={() => { setReversalModal(null); setReversalError(""); }}
                disabled={reversalLoading}
                style={{ border: "1px solid #d1d5db", background: "#fff", borderRadius: 6, padding: "8px 20px", cursor: "pointer" }}
              >
                Hayır
              </button>
              <button
                type="button"
                onClick={onConfirmReversal}
                disabled={reversalLoading}
                style={{ border: "none", background: "#dc2626", color: "#fff", borderRadius: 6, padding: "8px 20px", cursor: "pointer", fontWeight: 600 }}
              >
                {reversalLoading ? "İşleniyor..." : "Evet, Ters Al"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </Stack>
  );
}
